import { useState, useMemo, useEffect, useRef, FC } from 'react'
import { CategorizedModule, ResolvedFunction } from '../../types/functions'
import { Search, X, ChevronDown, ChevronRight } from 'lucide-react'

interface FunctionSidebarProps {
  modules: CategorizedModule[]
  activeFunctionId: string | null
  activeCategoryId?: string | null
  onSelectFunction: (fn: ResolvedFunction) => void
  onSelectCategory?: (categoryName: string, moduleName: string) => void
  searchQuery: string
  onSearchChange: (query: string) => void
}

export const FunctionSidebar: FC<FunctionSidebarProps> = ({
  modules,
  activeFunctionId,
  activeCategoryId,
  onSelectFunction,
  onSelectCategory,
  searchQuery,
  onSearchChange
}) => {
  // Track open/collapsed categories
  const [collapsedCategories, setCollapsedCategories] = useState<Record<string, boolean>>({})
  const [selectedModuleFilter, setSelectedModuleFilter] = useState<string>('all')
  const navRef = useRef<HTMLElement | null>(null)

  const toggleCategory = (key: string) => {
    setCollapsedCategories(prev => ({
      ...prev,
      [key]: !prev[key]
    }))
  }

  // Filter modules/categories/functions based on search & module tab
  const filteredModules = useMemo(() => {
    const q = searchQuery.trim().toLowerCase()

    let baseModules = modules
    if (selectedModuleFilter !== 'all') {
      baseModules = modules.filter(m => m.name.toLowerCase().includes(selectedModuleFilter.toLowerCase()))
    }

    if (!q) return baseModules

    return baseModules
      .map(mod => {
        const filteredCategories = mod.categories
          .map(cat => {
            const filteredFunctions = cat.functions.filter(fn => {
              const nameMatch = fn.name.toLowerCase().includes(q)
              const displayNameMatch = fn.displayName.toLowerCase().includes(q)
              const catMatch = fn.category.toLowerCase().includes(q)
              const descMatch = fn.description.toLowerCase().includes(q) || fn.remarks.toLowerCase().includes(q)
              const paramMatch = fn.parameters.some(
                p => p.name.toLowerCase().includes(q) || p.typeString.toLowerCase().includes(q)
              )
              return nameMatch || displayNameMatch || catMatch || descMatch || paramMatch
            })
            return {
              ...cat,
              functions: filteredFunctions
            }
          })
          .filter(cat => cat.functions.length > 0)

        return {
          ...mod,
          categories: filteredCategories
        }
      })
      .filter(mod => mod.categories.length > 0)
  }, [modules, searchQuery, selectedModuleFilter])

  const totalFilteredCount = useMemo(() => {
    return filteredModules.reduce(
      (acc, mod) => acc + mod.categories.reduce((cAcc, cat) => cAcc + cat.functions.length, 0),
      0
    )
  }, [filteredModules])

  // Scroll active item into view inside the sidebar without moving window
  useEffect(() => {
    if (!activeFunctionId || !navRef.current) return
    const activeEl = navRef.current.querySelector('.ns-sidebar-function-link.is-active') as HTMLElement | null
    if (activeEl && navRef.current) {
      const nav = navRef.current
      const navRect = nav.getBoundingClientRect()
      const elRect = activeEl.getBoundingClientRect()
      if (elRect.top < navRect.top || elRect.bottom > navRect.bottom) {
        const targetScroll =
          activeEl.offsetTop - nav.offsetTop - nav.clientHeight / 2 + activeEl.clientHeight / 2
        nav.scrollTo({ top: Math.max(0, targetScroll), behavior: 'smooth' })
      }
    }
  }, [activeFunctionId])

  const getBadgeStyle = (badge: string) => {
    switch (badge) {
      case 'FUNC':
        return 'ns-badge-func'
      case 'BROWSER':
        return 'ns-badge-browser'
      case 'ASYNC':
        return 'ns-badge-async'
      default:
        return 'ns-badge-default'
    }
  }

  const countsByModule = useMemo(() => {
    const counts: Record<string, number> = { all: 0 }
    for (const mod of modules) {
      const c = mod.categories.reduce((acc, cat) => acc + cat.functions.length, 0)
      counts[mod.name] = c
      counts.all += c
    }
    return counts
  }, [modules])

  return (
    <aside className="ns-function-sidebar">
      {/* Module Filter Tabs */}
      {modules.length > 1 && (
        <div className="ns-sidebar-module-tabs">
          <button
            type="button"
            className={`ns-module-tab-btn ${selectedModuleFilter === 'all' ? 'is-active' : ''}`}
            onClick={() => setSelectedModuleFilter('all')}
          >
            All <span className="ns-tab-badge">{countsByModule.all}</span>
          </button>
          {modules.map(mod => {
            const isSelected =
              selectedModuleFilter.toLowerCase() === mod.name.toLowerCase() ||
              (selectedModuleFilter === 'basic' && mod.name.toLowerCase().includes('basic')) ||
              (selectedModuleFilter === 'browser' && mod.name.toLowerCase().includes('browser'))

            return (
              <button
                key={mod.name}
                type="button"
                className={`ns-module-tab-btn ${isSelected ? 'is-active' : ''}`}
                onClick={() => setSelectedModuleFilter(mod.name)}
                title={mod.name}
              >
                {mod.name.replace(' Functions', '')}
                <span className="ns-tab-badge">{countsByModule[mod.name] || 0}</span>
              </button>
            )
          })}
        </div>
      )}

      {/* Search Bar */}
      <div className="ns-function-sidebar-search">
        <div className="ns-search-input-wrapper">
          <Search className="ns-search-icon" size={16} />
          <input
            type="text"
            placeholder="Search functions & params..."
            value={searchQuery}
            onChange={e => onSearchChange(e.target.value)}
            className="ns-search-input"
          />
          {searchQuery && (
            <button
              onClick={() => onSearchChange('')}
              className="ns-search-clear-btn"
              title="Clear search"
              type="button"
            >
              <X size={14} />
            </button>
          )}
        </div>
        {searchQuery && (
          <div className="ns-search-result-count">
            {totalFilteredCount} {totalFilteredCount === 1 ? 'function' : 'functions'} found
          </div>
        )}
      </div>

      {/* Navigation Tree */}
      <nav className="ns-function-sidebar-nav" ref={navRef}>
        {filteredModules.length === 0 ? (
          <div className="ns-sidebar-empty">No functions match your search.</div>
        ) : (
          filteredModules.map(mod => (
            <div key={mod.name} className="ns-sidebar-module-section">
              <div className="ns-sidebar-module-title">{mod.name}</div>

              {mod.categories.map(cat => {
                const categoryKey = `${mod.name}::${cat.name}`
                const isCollapsed = !!collapsedCategories[categoryKey] && !searchQuery
                const isCatActive = activeCategoryId === categoryKey || activeCategoryId === cat.name

                return (
                  <div key={cat.name} className={`ns-sidebar-category-group ${isCatActive ? 'is-cat-active' : ''}`}>
                    <div className="ns-sidebar-category-header-row">
                      <button
                        type="button"
                        className="ns-sidebar-category-chevron-btn"
                        onClick={() => toggleCategory(categoryKey)}
                        title={isCollapsed ? 'Expand category' : 'Collapse category'}
                      >
                        {isCollapsed ? <ChevronRight size={14} /> : <ChevronDown size={14} />}
                      </button>

                      <button
                        type="button"
                        className={`ns-sidebar-category-title-btn ${isCatActive ? 'is-active' : ''}`}
                        onClick={() => {
                          if (onSelectCategory) {
                            onSelectCategory(cat.name, mod.name)
                          } else {
                            toggleCategory(categoryKey)
                          }
                        }}
                        title={`View ${cat.name} overview`}
                      >
                        <span className="ns-category-title">{cat.name}</span>
                        <span className="ns-category-count">{cat.functions.length}</span>
                      </button>
                    </div>

                    {!isCollapsed && (
                      <ul className="ns-sidebar-function-list">
                        {cat.functions.map(fn => {
                          const isActive = activeFunctionId === fn.id || activeFunctionId === fn.anchorId

                          return (
                            <li key={fn.id} className="ns-sidebar-function-item">
                              <a
                                href={`#${fn.anchorId}`}
                                onClick={e => {
                                  e.preventDefault()
                                  onSelectFunction(fn)
                                }}
                                className={`ns-sidebar-function-link ${isActive ? 'is-active' : ''}`}
                              >
                                <span className={`ns-function-badge ${getBadgeStyle(fn.badge)}`}>
                                  {fn.badge}
                                </span>
                                <span className="ns-function-name" title={fn.name}>
                                  {fn.name}
                                </span>
                              </a>
                            </li>
                          )
                        })}
                      </ul>
                    )}
                  </div>
                )
              })}
            </div>
          ))
        )}
      </nav>
    </aside>
  )
}
